/*************************************************************************************
BfxAutoCompleteComponent is based on the @bfx/primeng component developed by PrimeTek,
used under the following license:

The MIT License (MIT)

Copyright (c) 2016-2017 PrimeTek

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
******************************************************************************************/

import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  Renderer2,
  forwardRef,
  ChangeDetectorRef,
  IterableDiffers,
  OnDestroy,
  Injector,
  ChangeDetectionStrategy,
  TemplateRef,
  NgZone,
} from "@angular/core";

import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { DOM_HANDLER_MODE_TOKEN, DomHandler, DomHandlerService } from "@bfx/primeng/dom";
import { ObjectUtils } from "@bfx/primeng/utils";
import { BfxInputControlStateEnum } from "../../shared/base-components/models/base-components.enums";

export const BfxAutoCompleteComponentState = BfxInputControlStateEnum;
export type BfxAutoCompleteComponentState = BfxInputControlStateEnum;

export const AUTOCOMPLETE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxAutoCompleteComponent),
  multi: true,
};

export const AUTOCOMPLETE_VIEW_PROVIDERS = [BfxInputDirective, BfxFieldSelectDirective];

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxAfterViewInitPromise } from "../bfx-common/helpers/bfx-after-view-init-promise";
import { OELoggerService } from "../order-entry/services/basic/oe-logger.service";
import { DisabledMixin, DisabledMixinInputs } from "../../shared/base-components/mixins/disabled";
import { EmphasisMixin } from "../../shared/base-components/mixins/emphasis";
import {
  FieldStateMixin,
  FieldStateMixinInputs,
} from "../../shared/base-components/mixins/field-state";
import {
  InputLabelMixin,
  InputLabelMixinInputs,
} from "../../shared/base-components/mixins/input-label";
import {
  InputLabelStateMixin,
  InputLabelStateMixinInputs,
} from "../../shared/base-components/mixins/label-state";
import { InputtedMixin, InputtedMixinInputs } from "../../shared/base-components/mixins/inputted";
import {
  MaxLengthMixin,
  MaxLengthMixinInputs,
} from "../../shared/base-components/mixins/max-length";
import { ComponentMixin } from "../../shared/base-components/mixins/mixin";
import { PasswordMixin, PasswordMixinInputs } from "../../shared/base-components/mixins/password";
import {
  PlaceholderMixin,
  PlaceholderMixinInputs,
} from "../../shared/base-components/mixins/placeholder";
import { RequiredMixin, RequiredMixinInputs } from "../../shared/base-components/mixins/required";
import { SizeMixin, SizeMixinInputs } from "../../shared/base-components/mixins/size";
import {
  MinLengthMixin,
  MinLengthMixinInputs,
} from "../../shared/base-components/mixins/min-length";
import { BfxInputDirective } from "../../shared/base-components/bfx-input";
import { BfxFieldSelectDirective } from "../../shared/base-components/bfx-field-select";
import {
  FieldIconsMixin,
  FieldIconsMixinInputs,
} from "../../shared/base-components/mixins/field-icons";
import {
  FieldSuffixMixin,
  FieldSuffixMixinInputs,
} from "../../shared/base-components/mixins/field-suffix";
import qcompare from "qcompare";
import { Subject, Subscription } from "rxjs";
import { NativeDesktopSupport } from "app/bfx-native-desktop/native-desktop-support";

const COLLATOR = new Intl.Collator(new Intl.NumberFormat().resolvedOptions().locale, {
  sensitivity: "base",
});

const Mixins = InputLabelMixin(
  EmphasisMixin(
    SizeMixin(
      FieldIconsMixin(
        FieldStateMixin(
          FieldSuffixMixin(
            InputtedMixin(
              InputLabelStateMixin(
                PlaceholderMixin(
                  PasswordMixin(
                    MinLengthMixin(MaxLengthMixin(RequiredMixin(DisabledMixin(ComponentMixin)))),
                  ),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  ),
);

@Component({
  selector: "bfx-autoComplete",
  templateUrl: "./html/bfx-autocomplete.html",
  host: {
    "[class.ui-inputwrapper-focus]": "focus",
  },
  providers: [DomHandler, ObjectUtils, AUTOCOMPLETE_VALUE_ACCESSOR],
  viewProviders: AUTOCOMPLETE_VIEW_PROVIDERS,
  inputs: [
    ...DisabledMixinInputs,
    ...FieldIconsMixinInputs,
    ...FieldStateMixinInputs,
    ...FieldSuffixMixinInputs,
    ...InputLabelMixinInputs,
    ...InputLabelStateMixinInputs,
    ...InputtedMixinInputs,
    ...MinLengthMixinInputs,
    ...MaxLengthMixinInputs,
    ...PasswordMixinInputs,
    ...PlaceholderMixinInputs,
    ...RequiredMixinInputs,
    ...SizeMixinInputs,
  ],
  outputs: ["fieldIconClick"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxAutoCompleteComponent
  extends Mixins
  implements AfterViewInit, OnDestroy, ControlValueAccessor, BfxAfterViewInitPromise
{
  // NEW NEW PARAMETERS (The Alternates if you will)
  /* When "useAlternateBlur" is set to true the blur flow will go through alternateBlur() function, 
        which addresses the OMS-36058 issue, where filtering the list and selecting item from the list (other than the first one) 
        results in the incorrect selection (the first one being always selected) in openFin. The alternateBlur also has better 
        handling of the ignoreDropdownBlur which is now built in (compared to the regularBlur() where its optional), 
        that prevents clicking on the dropdown button to produce blur events. In addition, by using the forceSelection option (configurable on off)
        in the forces the same behavior (select first) when the user clicks outside of the control as is the case with tab out.
        Finally the generic validation in alternateBlur is more consistent and solves the validation problem when regularBlur() is used with 
        the ignoreDropdownBlur and skipNativeBlurFormatting.
    */
  @Input("useAlternateBlur") useAlternateBlur: boolean = false;

  /* The  alternateConfig is an object which contains configuration option only for the alternateBlur() function.
        - allowEmpty - when set to 'true' will treat empty input as INACTIVE instead of INVALID
        - forceSelection - will select the first item of the available suggestions when the user clicks outside of the control,
                        given that there is more than one suggestion available. This is consistent with the default tab-out behavior.
        - customValidation - this option provides the ability to attach external validation function to the alternateBlur().
                dummy example: 
                    function (this: any, value: any, suggestions: Array<any>): BfxAutoCompleteComponentState  {
				        return value != null ? BBfxAutoCompleteComponentState.VALID: fxAutoCompleteComponentState.INVALID;
			        } 


        IMPORTANT NOTE: In order for external alternateConfig to be taken into consideration both 'allowEmpty' and 'forceSelection'
            must be specified, otherwise alternateConfigDefaults values will be used.
    */
  @Input("alternateConfig") alternateConfig: any = null;
  private alternateConfigDefaults: {
    allowEmpty: boolean;
    forceSelection: boolean;
    customValidation: any;
  } = {
    allowEmpty: false,
    forceSelection: false,
    customValidation: null,
  };

  // NEW PARAMETERS
  /* This parameter was introduced in order to correct problems with autocomplete validation in order entry defaults */
  @Input("settingsCorrection") settingsCorrection: boolean = false;
  /** If 'true' will skip this components onBlur processing, this avoids value formating to string in certain scenarios,
       use it if you have a wrapper around this component example: bfx-autocomplete-input */
  @Input("skipBlur") skipNativeBlurFormatting: boolean = false;
  /* If 'true' this parameter tweaks the validation for cases where null or empty (''),
       value of the model should be treaded as inactive instead of inactive  */
  @Input("emptyIsInactive") setEmptyToInactive: boolean = false;
  /* If 'true' this parameter will skip the sorting of the suggestions when loaded */
  @Input("skipSorting") skipSuggestionsSort: boolean = false;

  /* p-dataTable editable mode support */
  @Input("skipShowTargetInvisible") skipShowTargetInvisible: boolean = false;

  @Input("target") target: string = "generic";
  /* p-dataTable editable mode tab helper */

  @Input("proxyTab")
  public tabForward: Function = null;

  @Input("tooltipOnItem")
  public tooltipOnItem: boolean = false;

  afterViewInitResolve?: Function;

  public afterViewInitReady = new Promise<void>((res) => {
    this.afterViewInitResolve = res;
  });

  fieldPointerOver: boolean = false;

  dropdownListOver: boolean = false;

  // BASIC PARAMETERS

  @Input() minLength: number = 1;

  @Input() delay: number = 300;

  @Input() style: any;

  @Input() styleClass: string;

  @Input() inputStyle: any;

  @Input() inputId: string;

  @Input() inputStyleClass: string;

  @Input() placeholder: string = null;

  @Input() readonly: boolean;

  alignSubject = new Subject<void>();
  suggestionsChangedSubject = new Subject<void>();

  ngZone: NgZone;
  alignSubscription: Subscription;
  suggestionsChangedSubscription: Subscription;
  focusInputOnSelect: boolean = false;

  get disabled() {
    return super.disabled;
  }

  @Input()
  public set disabled(value: boolean) {
    super.disabled = value;
    this.state = this._state;
  }

  @Input() maxlength: number;

  @Input() dropdownMenuClass: string = "";

  get dropdownMenuClasses() {
    return {
      "bfx-dropdown-multi-select-item-container": this.multiple,
      "bfx-dropdown-single-select-item-container": !this.multiple,
      "bfx-dropdown-hidden": !this.panelVisible,
      "bfx-dropdown-lg": this.lg,
      "bfx-dropdown-md": this.md,
      "bfx-dropdown-sm": this.sm,
      [this.dropdownMenuClass]: this.dropdownMenuClass != null,
    };
  }

  @Input() required: boolean;

  @Input() appendTo: Element | string | null = "body";

  @Input() autoHighlight: boolean;

  @Input() autoHighlightIndex: number = -1;

  @Input() autoHighlightFirstIfNoValue: boolean = true;

  @Input("clearUnmatchedInputOnBlur") clearUnmatchedInputOnBlur: boolean = false;

  @Input() forceSelection: boolean = true;

  @Input() type: string = "search";

  @Input() inputStartContainer: TemplateRef<Element> = null;
  @Input() inputEndContainer: TemplateRef<Element> = null;
  @Input() fieldStartContainer: TemplateRef<any>;
  @Input() fieldEndContainer: TemplateRef<Element> = null;

  @Input() public hideField: boolean = false;

  public get label(): string {
    return this.inputLabelText;
  }

  @Input()
  public set label(value: string) {
    this.inputLabelText = value;
    this.cd.markForCheck();
  }

  private _state: BfxAutoCompleteComponentState = BfxAutoCompleteComponentState.INACTIVE;
  public get state(): BfxAutoCompleteComponentState {
    return this._state;
  }
  @Input()
  public set state(value: BfxAutoCompleteComponentState) {
    this._state = value;
    switch (value) {
      case BfxAutoCompleteComponentState.ACTIVE: {
        this.inputLabelState = "enabled";
        this.fieldState = "active";
        break;
      }
      case BfxAutoCompleteComponentState.VALID: {
        this.inputLabelState = "valid";
        this.fieldState = this.disabled ? "disabled" : "enabled";
        break;
      }
      case BfxAutoCompleteComponentState.INVALID: {
        this.inputLabelState = this.disabled ? "disabled" : "enabled";
        this.fieldState = "invalid";
        break;
      }
      case BfxAutoCompleteComponentState.NONE:
      case BfxAutoCompleteComponentState.INACTIVE:
      default: {
        this.inputLabelState = this.disabled ? "disabled" : "enabled";
        this.fieldState = this.disabled ? "disabled" : "enabled";
        break;
      }
    }

    this.inputLabelStateChange.emit(this.inputLabelState);
    this.fieldStateChange.emit(this.fieldState);
  }

  @Output() completeMethod: EventEmitter<any> = new EventEmitter();

  @Output() onSelect: EventEmitter<any> = new EventEmitter();

  @Output() onUnselect: EventEmitter<any> = new EventEmitter();

  @Output() onFocus: EventEmitter<any> = new EventEmitter();

  @Output() onBlur: EventEmitter<any> = new EventEmitter();

  @Output() onDropdownClick: EventEmitter<any> = new EventEmitter();

  @Output() onClear: EventEmitter<any> = new EventEmitter();

  @Output() onShowDropdown: EventEmitter<any> = new EventEmitter();

  @Output() onHideDropdown: EventEmitter<any> = new EventEmitter();

  @Output() onKeyUp: EventEmitter<any> = new EventEmitter();

  @Output() onInvalidInput: EventEmitter<any> = new EventEmitter();

  @Output() onStateChanged: EventEmitter<any> = new EventEmitter();

  @Output() onSuggestionsChanged: EventEmitter<any> = new EventEmitter();

  @Input() checkDirtyBlur: boolean = false;

  @Input() field: string;
  @Input() selectedField: string;

  @Input() scrollHeight: string = "200px";

  @Input() dropdown: boolean;

  @Input() dropdownMode: string = "blank";

  @Input() multiple: boolean;

  @Input() tabindex: number;

  @Input() dataKey: string;

  @Input() emptyMessage: string;

  @Input() immutable: boolean = true;

  @Input() showBar: boolean = true;

  @Input() allowEmptySelection: boolean = false;

  @Input() allowEmptySelectionAsInvalid: boolean = false;

  @Input("convertedUTCTimeFixingList") convertedUTCTimeFixingList: string[] = [];

  @Input("getStateCallback")
  getStateCallback: () => BfxAutoCompleteComponentState;

  @ViewChild("in") inputEL: ElementRef;

  @ViewChild("multiIn") multiInputEL: ElementRef;

  private _panelEL: ElementRef;

  public get panelEL(): ElementRef {
    return this._panelEL;
  }

  @ViewChild("panel")
  public set panelEL(value: ElementRef) {
    this._panelEL = value;

    if (value && this.suggestionsUpdated) {
      this.suggestionsUpdated = true;
    }
  }

  private _panelParentEL: ElementRef;

  public get panelParentEL(): ElementRef {
    return this._panelParentEL;
  }

  @ViewChild("panelParent")
  public set panelParentEL(value: ElementRef) {
    this._panelParentEL = value;
  }

  @ViewChild("multiContainer") multiContainerEL: ElementRef;

  @ViewChild("ddBtn") dropdownButton: ElementRef;

  private showOnSuggestionsChanged: boolean = false;

  value: any;

  /**
   * Index of the current autocomplete value in the suggestion list
   */
  public selectedIndex: number = -1;

  _dataChanged: boolean = true;

  _suggestions: readonly any[];

  onModelChange: Function = () => {};

  onModelTouched: Function = () => {};

  timeout: NodeJS.Timeout;

  panelVisible: boolean = false;

  /** Remove listener for document click events */
  protected documentClickListener: () => void;

  private _suggestionsUpdated: boolean;

  public get suggestionsUpdated(): boolean {
    return this._suggestionsUpdated;
  }

  public set suggestionsUpdated(value: boolean) {
    this._suggestionsUpdated = value;

    if (value && this.panelEL?.nativeElement && this.panelEL?.nativeElement?.offsetParent) {
      this._suggestionsUpdated = false;
      this.alignPanel();
    }
  }

  highlightOption: any;

  public highlightOptionChanged() {
    setTimeout(() => {
      let listItem = DomHandler.findSingle(
        this.panelEL.nativeElement,
        ".bfx-dropdown-single-select-item-hover",
      );
      if (listItem) {
        DomHandler.scrollInView(this.panelEL.nativeElement, listItem);
      }
    }, 0);
  }

  isInputNotEmpty: boolean = false;

  inputClick: boolean;

  inputKeyDown: boolean;

  noResults: boolean;

  differ: any;

  inputFieldValue: string | null = null;

  oldValue: any = {};

  loading: boolean = false;

  suggestionsPending: boolean = true;

  isDestroy: boolean = false;

  protected skipInputBlur: boolean = false;

  public domHandlerService: DomHandlerService;

  constructor(
    injector: Injector,
    public inputDirective: BfxInputDirective,
    public fieldDirective: BfxFieldSelectDirective,
    public el: ElementRef,
    public domHandler: DomHandler,
    public renderer: Renderer2,
    public objectUtils: ObjectUtils,
    public cd: ChangeDetectorRef,
    public differs: IterableDiffers,
    protected oeLogger: OELoggerService,
  ) {
    super(injector);
    this.ngZone = injector.get(NgZone);
    this.oeLogger = this.oeLogger.for(__dirname);
    this.domHandlerService = injector.get(DOM_HANDLER_MODE_TOKEN);
    this.differ = differs.find([]).create(null);
    this.alternateConfig = Object.assign({}, this.alternateConfigDefaults);

    this.fieldEndIcon = this.defaultDropdownIcon();
  }

  private defaultDropdownIcon(): string | string[] {
    return "bfx-ignore-target icon-ion_chevron_down bfx-field-select-button-toggle";
  }

  get suggestions(): readonly any[] {
    return this._suggestions;
  }

  @Input()
  set suggestions(val: readonly any[]) {
    if (val != null && !this.skipSuggestionsSort) {
      if (this.field != null) {
        val = val.toSorted((a, b) => (a[this.field] as string).localeCompare(b[this.field]));
      } else {
        val = val.toSorted((a, b) => a.localeCompare(b));
      }
    }

    if (this.immutable || qcompare(this._suggestions, val) == false) {
      this._suggestions = Object.freeze(val);
      this.cd.markForCheck();
      this.suggestionsChangedSubject.next();
    }

    if (this.settingsCorrection) {
      if (this.suggestionsPending && this._suggestions && this._suggestions.length > 0) {
        this.suggestionsPending = false;
        this.dataChanged = true;
        this.validate();
      }
    }
  }

  @Input()
  get dataChanged(): boolean {
    return this._dataChanged;
  }

  set dataChanged(value: boolean) {
    this._dataChanged = value;
    if (this.settingsCorrection) {
      if (!this.value) {
        this.state = this.getAutoCompleteState(BfxAutoCompleteComponentState.VALID);
      }
    } else {
      if (!value) {
        this.state = this.getAutoCompleteState(BfxAutoCompleteComponentState.INACTIVE);
      }
    }
  }

  handleSuggestionsChange() {
    let suggestionsUpdated = false;
    let highlightOption = null;
    this.selectedIndex = -1;

    this.cd.markForCheck();

    if (Array.isArray(this.suggestions) && this.suggestions.length > 0) {
      this.noResults = false;

      if (this.showOnSuggestionsChanged) {
        this.showOnSuggestionsChanged = false;
        this.show(!this.panelVisible);
      }

      suggestionsUpdated = true;

      let searchValue: string = this.getInputQuery();
      if (searchValue != null && searchValue.length > 0) {
        // if we have a search value

        if (this.suggestions.length == 1) {
          // if only one , we select it
          highlightOption = this.suggestions[0];
        } else if (this.suggestions.length > 1) {
          // or more than one - pick the match if there, else first one
          highlightOption = this.suggestions[0];
          for (let i = 0; i < this.suggestions.length; i++) {
            const current = this.suggestions[i];

            if (searchValue == this.getOptionLabel(current)) {
              highlightOption = current;
              this.selectedIndex = i;
              break;
            }
          }
        }
      } else {
        //we hadn't searched for anything
        if (this.autoHighlight) {
          if (this.autoHighlightIndex >= 0 && this.autoHighlightIndex < this._suggestions.length) {
            // so highlight what we were created with
            highlightOption = this.suggestions[this.autoHighlightIndex];
          } else if (this.autoHighlightFirstIfNoValue) {
            // if no value - and enabled - select the first one
            highlightOption = this.suggestions[0];
          }
        }
      }
    } else {
      this.noResults = true;

      if (this.emptyMessage && this.showOnSuggestionsChanged) {
        this.showOnSuggestionsChanged = false;
        this.show(!this.panelVisible);
        suggestionsUpdated = true;
      } else {
        this.showOnSuggestionsChanged = false;
        this.hide();
      }
    }

    this.loading = false;
    if (suggestionsUpdated) {
      this.highlightOption = highlightOption;
      this.suggestionsUpdated = true;
    }

    this.onSuggestionsChanged.emit();
  }

  private getInputQuery(): string {
    if (!this.multiple)
      return typeof this.inputEL?.nativeElement?.value === "string"
        ? this.inputEL.nativeElement.value
        : "";

    return typeof this.multiInputEL?.nativeElement?.value === "string"
      ? this.multiInputEL.nativeElement.value
      : "";
  }

  ngAfterViewInit() {
    if (this.appendTo) {
      if (this.appendTo === "body")
        DomHandler.getBody(this.el.nativeElement).appendChild(this.panelEL.nativeElement);
      else DomHandler.appendChild(this.panelEL.nativeElement, this.appendTo);
    }
    if (this.settingsCorrection && this.value) {
      this.validate();
    }

    // ALTERNATE BLUR
    // reset alternate config to default if supplied config is missing values
    if (
      this.alternateConfig == null ||
      typeof this.alternateConfig.allowEmpty !== "boolean" ||
      typeof this.alternateConfig.forceSelection !== "boolean"
    ) {
      this.alternateConfig = Object.assign({}, this.alternateConfigDefaults);
    }

    this.alignSubscription = this.alignSubject.subscribe(() => {
      setTimeout(() => this.handleAlignPanel(), 0);
    });
    this.suggestionsChangedSubscription = this.suggestionsChangedSubject.subscribe(() => {
      setTimeout(() => this.handleSuggestionsChange(), 0);
    });

    this.afterViewInitResolve();
  }

  ngDestroy() {
    if (this.alignSubscription != null) {
      this.alignSubscription.unsubscribe();
      this.alignSubscription = null;
    }
  }

  getAutoCompleteState(
    providedState: BfxAutoCompleteComponentState = null,
  ): BfxAutoCompleteComponentState {
    let ret: BfxAutoCompleteComponentState = providedState;
    if (null != this.getStateCallback) {
      let callback = this.getStateCallback();
      if (null != callback) {
        if (callback !== providedState) {
          logger.out.info(
            `${this.constructor.name}.getAutoCompleteState() override ${BfxAutoCompleteComponentState[providedState]} with ${BfxAutoCompleteComponentState[callback]}`,
          );
        }

        ret = callback;
      }
    }
    return ret;
  }

  protected validate(): void {
    if (!this.value && this.setEmptyToInactive) {
      this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.VALID));
    } else if (this.value) {
      if (
        this.suggestions &&
        this.suggestions.length > 0 &&
        this.inputFieldValue != null &&
        this.inputFieldValue.length > 0
      ) {
        let valid = false;
        for (let suggestion of this.suggestions) {
          let itemValue = this.field
            ? ObjectUtils.resolveFieldData(suggestion, this.field)
            : suggestion;
          if (
            itemValue != null &&
            itemValue.toLowerCase().indexOf(this.inputFieldValue.toLowerCase()) >= 0
          ) {
            valid = true;
            break;
          }
        }
        if (valid) {
          this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.VALID));
        } else {
          this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.INVALID));
        }
      }
    }
  }

  writeValue(value: any): void {
    this.value = value;
    this.updateInputField();
    if (this.settingsCorrection) {
      if (this.value && this.suggestions && this.suggestions.length > 0) {
        this.dataChanged = true;
      }
      this.validate();
    }
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
  }

  onInput(event: KeyboardEvent) {
    if (!this.inputKeyDown) {
      logger.out.warn(`${this.constructor.name}.onInput ignoring subsequent key %O`, event);
      return;
    }

    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    let value = (<HTMLInputElement>event?.target)?.value;

    if (typeof value === "string" && value.length >= this.minLength) {
      this.timeout = setTimeout(() => {
        this.search(event, value);
        this.timeout = null;
      }, this.delay);
    } else {
      if (!this.settingsCorrection) {
        this.suggestions = null;
      }
      this.hide();
      this.onClear.emit(event);
      this.selectItem(null, true);
      this.updateInputtedState();
      this.inputKeyDown = false;
    }
  }

  onInputClick(event: MouseEvent) {
    if (this.documentClickListener) {
      this.inputClick = true;
    }
    this.markForCheck();
  }

  onFieldPointerOver(_) {
    if (this.fieldState == "enabled") {
      this.fieldPointerOver = true;
      this.fieldState = "hover";
      this.fieldStateChange.emit(this.fieldState);
      this.cd.markForCheck();
    }
  }

  onFieldPointerOut(_) {
    if (this.fieldState != "active") {
      this.fieldPointerOver = false;
      this.fieldState =
        this.state === BfxAutoCompleteComponentState.INVALID
          ? "invalid"
          : this.disabled
            ? "disabled"
            : "enabled";
      this.fieldStateChange.emit(this.fieldState);
      this.cd.markForCheck();
    } else {
      // state changes deferred until blur completes.
      this.fieldPointerOver = false;
    }
  }

  onListPointerOver(_) {
    this.dropdownListOver = true;
    this.onFieldPointerOver(_);
    this.cd.markForCheck();
  }

  onListPointerOut(_) {
    this.dropdownListOver = false;
    this.onFieldPointerOut(_);
    this.cd.markForCheck();
  }

  search(event: any, query: string) {
    //allow empty string but not undefined or null
    if (query === undefined || query === null) {
      return;
    }

    this.showOnSuggestionsChanged = true;
    this.loading = true;

    this.completeMethod.emit({
      originalEvent: event,
      query: query,
    });

    this.cd.markForCheck();
  }

  selectItem(option: any, focus = this.focusInputOnSelect) {
    logger.out.info(`${this.constructor.name}.selectItem(): INIT -> option: %o`, option);
    // support find (OnPush cd strategy)
    this.markForCheck();

    this.fieldPointerOver = false;
    this.dropdownListOver = false;

    if (this.multiple) {
      this.multiInputEL.nativeElement.value = "";
      this.updateIsNotEmpty();
      this.value = this.value || [];
      if (!this.isSelected(option)) {
        this.value = [...this.value, option];
        this.onModelChange(this.value);
      }
    } else {
      let optionLabel = this.getOptionLabel(option);
      this.inputEL.nativeElement.value = optionLabel;

      this.updateIsNotEmpty();
      this.value = option;
      logger.out.info(
        `${this.constructor.name}.selectItem(): ASSIGNMENT -> this.value: %o`,
        this.value,
      );

      logger.out.info(
        `${this.constructor.name}.selectItem(): VALIDATION -> this.state: ${
          this.state in BfxAutoCompleteComponentState
            ? BfxAutoCompleteComponentState[this.state]
            : this.state
        }`,
      );

      logger.out.info(
        `${this.constructor.name}.selectItem(): MODEL CHANGE -> this.value: %o`,
        this.value,
      );
      this.onModelChange(this.value);
    }

    logger.out.info(`${this.constructor.name}.selectItem(): EMIT -> option: %o`, option);
    this.onSelect.emit(option);

    // Selection can come from a mouse or keyboard event, or through the API. We only want
    // to set state to valid/invalid when focus is not set.
    if (focus) {
      this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.ACTIVE));
    } else {
      this.updateState();
    }

    this.updateInputtedState();

    this.onHideDropdown.emit();

    if (focus) {
      // If dropdown was active, then refocus to input
      this.focusInput();
    }
  }

  private updateState() {
    this.setEmptyToInactive && this.isInputNotEmpty === false
      ? this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.VALID))
      : this.isInputNotEmpty
        ? this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.VALID))
        : this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.INVALID));
  }

  private getOptionLabel(option: any) {
    return option
      ? this.selectedField
        ? ObjectUtils.resolveFieldData(option, this.selectedField) || ""
        : this.field
          ? ObjectUtils.resolveFieldData(option, this.field) || ""
          : option
      : "";
  }

  show(emitEvent: boolean = true) {
    this.afterViewInitReady.then(() => {
      if (this.multiInputEL || this.inputEL) {
        let element: Element = this.multiple
          ? this.multiInputEL.nativeElement
          : this.inputEL.nativeElement;
        let hasFocus = element.ownerDocument.activeElement == element;
        if (!this.panelVisible && hasFocus) {
          if (
            NativeDesktopSupport.isNativeDesktopContainer() &&
            NativeDesktopSupport.enableDropdowns != true
          ) {
            this.handleAlignPanel(false);
          }

          this.panelVisible = true;
          this.panelEL.nativeElement.style.zIndex = ++DomHandler.zindex;
          this.panelEL.nativeElement.style.display = "flex";
          if (this.tooltipOnItem) {
            this.panelEL.nativeElement.style.setProperty("overflow-x", "hidden");
          }
          let parentNode: Element = this.panelEL.nativeElement.parentNode;
          if (
            parentNode != null &&
            parentNode.nodeName.toLowerCase() == "body" &&
            element.ownerDocument != this.panelEL.nativeElement.ownerDocument
          ) {
            element.ownerDocument.body.appendChild(this.panelEL.nativeElement);
          }

          this.alignPanel();

          if (emitEvent) {
            this.onShowDropdown.emit();
          }

          this.bindDocumentClickListener();
        }
      }
    });
  }

  alignPanel() {
    this.alignSubject.next();
  }

  /* see: this.alignPanel()
      - this must run after cd tick to have valid panel contents
    */
  private handleAlignPanel(abortIfPanelNotVisible: boolean = true) {
    if ((this.suggestionsUpdated == true || this.loading) && abortIfPanelNotVisible === true) {
      // don't align during an update / before template engine has picked up change
      this.cd.markForCheck();
      this.suggestionsUpdated = true;
      return;
    }

    let targetElement = this.multiple
      ? this.multiContainerEL?.nativeElement?.parentElement
      : this.inputEL?.nativeElement?.parentElement;

    if (this.panelVisible == false && abortIfPanelNotVisible === true) {
      return;
    }

    this.setPanelWidth(targetElement);

    if (this.appendTo) {
      if (
        this.skipShowTargetInvisible &&
        targetElement.offsetTop == 0 &&
        targetElement.offsetLeft == 0 &&
        targetElement.offsetWidth == 0 &&
        targetElement.offsetHeight == 0
      ) {
        logger.out.info(
          `${this.constructor.name}.align() failed to acquire valid absolutePosition`,
        );
        this.hide();
      } else {
        DomHandler.absolutePosition(this.panelEL.nativeElement, targetElement);
      }
    } else {
      DomHandler.relativePosition(this.panelEL.nativeElement, targetElement);
    }

    if (this.highlightOption != null) {
      this.highlightOptionChanged();
    }
  }

  private setPanelWidth(targetElement: Element) {
    if (targetElement?.parentElement != null && targetElement.parentElement instanceof Element) {
      const width = DomHandler.getWidth(targetElement.parentElement.parentElement);
      if (width > 0) {
        this.panelEL.nativeElement.style.minWidth = width + "px";
        this.panelEL.nativeElement.style.width = width + "px";
        this.panelEL.nativeElement.style.maxWidth = width + "px";
      }
    }
  }

  hide() {
    this.afterViewInitReady.then(() => {
      this.cd.markForCheck();
      this.onHideDropdown.emit();
      this.panelVisible = false;
      this.dropdownListOver = false;
      this.panelEL.nativeElement.style.display = "none";
      this.unbindDocumentClickListener();
    });
  }

  handleDropdownClick(event, index: number = 0) {
    if (this.panelVisible) {
      this.hide();
      this.focusInput();
      return;
    }

    if (index > 0) {
      return;
    }

    this.afterViewInitReady.then(() => {
      this.focusInput();
      let queryValue = this.multiple
        ? this.multiInputEL?.nativeElement?.value
        : this.inputEL?.nativeElement?.value;

      if (this.dropdownMode === "current") this.search(event, queryValue);
      else this.search(event, "");

      this.onDropdownClick.emit({
        originalEvent: event,
        query: queryValue,
      });
    });
  }

  focusInput() {
    if (this.multiple) this.multiInputEL?.nativeElement?.select?.();
    else this.inputEL?.nativeElement?.select?.();
  }

  blurInput() {
    if (this.multiple) this.multiInputEL?.nativeElement?.blur?.();
    else this.inputEL?.nativeElement?.blur?.();
  }

  removeItem(item: any) {
    this.afterViewInitReady.then(() => {
      let itemIndex = DomHandler.index(item);
      let removedValue = this.value[itemIndex];
      this.value = this.value.filter((val, i) => i != itemIndex);
      this.onUnselect.emit(removedValue);
      this.onModelChange(this.value);
    });
  }

  onKeydown(event) {
    if (this.panelVisible) {
      let highlightItemIndex = this.findOptionIndex(this.highlightOption);

      switch (event.which) {
        //down
        case 40:
          if (highlightItemIndex != -1) {
            let nextItemIndex = highlightItemIndex + 1;
            if (nextItemIndex != this.suggestions.length) {
              this.highlightOption = this.suggestions[nextItemIndex];
              this.highlightOptionChanged();
            }
          } else if (this.suggestions != null && this.suggestions.length > 0) {
            this.highlightOption = this.suggestions[0];
          }

          event.preventDefault();
          break;

        //up
        case 38:
          if (highlightItemIndex > 0) {
            let prevItemIndex = highlightItemIndex - 1;
            this.highlightOption = this.suggestions[prevItemIndex];
            this.highlightOptionChanged();
          }

          event.preventDefault();
          break;

        //enter
        case 13:
          if (this.highlightOption) {
            this.selectItem(this.highlightOption, true);
            this.hide();
          }
          event.preventDefault();
          break;

        //escape
        case 27:
          this.hide();
          event.preventDefault();
          break;

        //tab
        case 9:
          if (this.highlightOption) {
            this.selectItem(this.highlightOption);
          }
          this.hide();

          if (this.tabForward) {
            event.preventDefault();

            this.tabForward(event);
          }
          break;
      }
    } else {
      if (event.which === 40) {
        this.search(event, event.target.value);
      }
    }

    if (this.multiple) {
      switch (event.which) {
        //backspace
        case 8:
          if (this.value && this.value.length && !this.multiInputEL.nativeElement.value) {
            this.value = [...this.value];
            let removedValue = this.value.pop();
            this.onUnselect.emit(removedValue);
            this.onModelChange(this.value);
          }
          break;
      }
    }

    this.inputKeyDown = true;
  }

  onKeyup(event) {
    this.onKeyUp.emit(event);
  }

  onInputFocus(event) {
    this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.ACTIVE));
    this.multiple
      ? this.multiInputEL?.nativeElement?.select?.()
      : this.inputEL?.nativeElement?.select?.();
    this.onFocus.emit(event);
    this.cd.markForCheck();
  }

  public onInputBlur($event: FocusEvent): void {
    this.useAlternateBlur ? this.alternateBlur($event) : this.regularBlur($event);
    this.cd.markForCheck();
  }

  protected alternateBlur($event: FocusEvent): void {
    const inputQuery = this.getInputQuery();
    let ignoreTarget = !(
      $event != null &&
      $event.relatedTarget != null &&
      $event.relatedTarget instanceof Element &&
      typeof $event.relatedTarget.tagName === "string" &&
      $event.relatedTarget.tagName.length > 0 &&
      Array.isArray($event.relatedTarget.classList) &&
      !$event.relatedTarget.classList.includes("bfx-ignore-target")
    );
    let valuesConsistent =
      (this.value != null &&
        typeof this.value === "object" &&
        this.field != null &&
        COLLATOR.compare(this.getOptionLabel(this.value), inputQuery) === 0) ||
      (typeof this.value === "string" && COLLATOR.compare(this.value, inputQuery) === 0);
    let ignoreBlurGuard =
      (valuesConsistent && ignoreTarget) ||
      (this.dropdownListOver && this.panelVisible); /* pending select item? */

    logger.out.info(`${this.constructor.name}.alternateBlur(): INIT -> event: %o`, $event);

    if (!this.isDestroy) {
      this.cd.markForCheck();
    }

    if (ignoreBlurGuard) {
      logger.out.info(`${this.constructor.name}.alternateBlur(): IGNORE Dropdown blur`);
      this.fieldPointerOver = false;
      this.dropdownListOver = false;
      this.updateState();
      return;
    }

    if (this.multiple) {
      logger.out.warn(`${this.constructor.name}.alternateBlur() is not supported`);
      return;
    }

    this.onModelTouched();
    // DECLARATION
    let value = null;
    let suggestionsCount: number =
      Array.isArray(this.suggestions) && this.suggestions.length > 0 ? this.suggestions.length : 0;
    let exactMatch =
      suggestionsCount > 0
        ? this.suggestions.find(
            (item) => COLLATOR.compare(this.getOptionLabel(item), inputQuery) === 0,
          )
        : null;

    // DEFINE RESULT/VALUE
    if (exactMatch != null) {
      value = exactMatch;
      logger.out.info(
        `${this.constructor.name}.alternateBlur(): RESULT -> EXACT MATCH = result:  %o`,
        value,
      );
    } else if (suggestionsCount === 1) {
      value = this.suggestions[0];
      logger.out.info(
        `${this.constructor.name}.alternateBlur(): RESULT -> ONLY 1 | SELECTED = result:  %o`,
        value,
      );
    } else if (suggestionsCount > 1) {
      let filteredSuggestions =
        typeof inputQuery === "string" &&
        inputQuery.length > 0 &&
        this.suggestions.filter(
          (item) => this.getOptionLabel(item).toLowerCase().indexOf(inputQuery.toLowerCase()) >= 0,
        );
      if (
        this.alternateConfig != null &&
        typeof this.alternateConfig.forceSelection === "boolean" &&
        this.alternateConfig.forceSelection &&
        Array.isArray(filteredSuggestions) &&
        filteredSuggestions.length > 0
      ) {
        value = filteredSuggestions[0];
        logger.out.info(
          `${this.constructor.name}.alternateBlur(): RESULT -> MANY | FORCE SELECT FIRST = result:  %o`,
          value,
        );
      } else {
        value = inputQuery;
        logger.out.info(
          `${this.constructor.name}.alternateBlur(): RESULT -> MANY | NO SELECT = result: %o`,
          value,
        );
      }
    } else {
      // suggestionsCount === 0
      value = inputQuery;
      logger.out.info(
        `${this.constructor.name}.alternateBlur(): RESULT -> NONE | STRING = result: %o`,
        value,
      );
    }

    /// APPLY Result & Emit
    this.inputEL.nativeElement.value = this.getOptionLabel(value);

    logger.out.info(
      `${this.constructor.name}.alternateBlur(): INPUT VALUE -> this.inputEL.nativeElement.value: ${this.inputEL.nativeElement.value}`,
    );

    this.value = value;
    this.updateIsNotEmpty();
    this.onModelChange(this.value);

    // VALIDATION
    // Custom validation
    let customValidation =
      this.alternateConfig != null &&
      this.alternateConfig.customValidation != null &&
      typeof this.alternateConfig.customValidation === "function"
        ? this.alternateConfig.customValidation
        : null;
    if (customValidation != null) {
      try {
        logger.out.info(`${this.constructor.name}.alternateBlur(): Custom validation invoked!`);
        let newState = customValidation.call(this, this.value, this.suggestions);
        if (newState in BfxAutoCompleteComponentState) {
          this.stateChange(newState);
          logger.out.info(
            `${this.constructor.name}.alternateBlur(): CUSTOM VALIDATION -> state: ${
              this.state in BfxAutoCompleteComponentState
                ? BfxAutoCompleteComponentState[this.state]
                : this.state
            }`,
          );
        } else {
          logger.out.error(
            `DEV: ${this.constructor.name}: INCORRECT Custom validation result type!`,
          );
        }
      } catch (ex) {
        logger.out.error(
          `${this.constructor.name}: Custom validation FAILED: ${ex} | validation function: %O`,
          customValidation,
        );
      }
    } else {
      // Generic validation
      let valueIsEmpty = this.value == null || this.value === "";
      if (valueIsEmpty) {
        if (
          this.alternateConfig != null &&
          typeof this.alternateConfig.allowEmpty === "boolean" &&
          this.alternateConfig.allowEmpty
        ) {
          this.stateChange(BfxAutoCompleteComponentState.INACTIVE);
        } else {
          this.stateChange(BfxAutoCompleteComponentState.INVALID);
        }
      } else if (suggestionsCount == 0) {
        this.stateChange(BfxAutoCompleteComponentState.INVALID);
      } else {
        if (this.suggestions.find((item) => item === this.value) != null) {
          this.stateChange(BfxAutoCompleteComponentState.VALID);
        } else {
          this.stateChange(BfxAutoCompleteComponentState.INVALID);
        }
      }
      logger.out.info(
        `${this.constructor.name}.alternateBlur(): G. VALIDATION -> state: ${
          this.state in BfxAutoCompleteComponentState
            ? BfxAutoCompleteComponentState[this.state]
            : this.state
        }`,
      );
    }
    this.onBlur.emit($event);
  }

  protected regularBlur($event: any) {
    let relatedTargetIsNotEmpty =
      $event != null &&
      $event.relatedTarget != null &&
      typeof $event.relatedTarget.tagName === "string" &&
      $event.relatedTarget.tagName.length > 0;
    if (!relatedTargetIsNotEmpty && this.dropdownListOver && this.panelVisible) {
      logger.out.info(`${this.constructor.name}.regularBlur(): IGNORE Dropdown blur`);
      this.fieldPointerOver = false;
      this.dropdownListOver = false;
      return;
    }

    logger.out.info(`${this.constructor.name}.regularBlur(): INIT -> event: %o`, $event);
    if (!this.isDestroy) this.cd.markForCheck();
    this.onModelTouched();

    if (!this.skipNativeBlurFormatting) {
      // added in order to be able to skip the validation when resetting the autocomplete
      if (this.forceSelection && this.suggestions) {
        let valid = false;
        let inputValue = $event.target.value; // keep inputValue case unless match suggestion
        let inputValueEmpty = inputValue.length == 0;
        let emptyAsInvalid = inputValueEmpty && this.allowEmptySelectionAsInvalid;

        logger.out.info(`${this.constructor.name}.regularBlur(): inputValue:${inputValue}`);

        if (inputValueEmpty && this.allowEmptySelection) {
          if (this.setEmptyToInactive) {
            // logger.out.info(`${this.constructor.name}.onInputBlur() BfxAutoCompleteComponentState.INACTIVE ${this.setEmptyToInactive}`);
            this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.VALID));
            if (this.settingsCorrection) {
              logger.out.info(`${this.constructor.name}.regularBlur(): EMIT EMPTY`);
              this.onBlur.emit($event);
            }
            return;
          }
          // if emptyAsInvalid is false, force selection will be attempted
        } else if (
          this.suggestions &&
          this.suggestions.length > 0 &&
          typeof inputValue === "string" &&
          inputValue.length > 0
        ) {
          let validSuggestion: any = null;
          let validInputValue = null;
          let exact = false;

          for (let suggestion of this.suggestions) {
            const itemValue = this.getOptionLabel(suggestion);
            if (
              typeof itemValue === "string" &&
              itemValue.toLowerCase().indexOf(inputValue.trim().toLowerCase()) >= 0
            ) {
              exact = COLLATOR.compare(itemValue, inputValue.trim()) === 0;

              if (exact || !valid) {
                // take exact always or use first valid
                validInputValue = itemValue;
                validSuggestion = suggestion;
                valid = true;
                logger.out.info(
                  `${this.constructor.name}.regularBlur(): SEARCH -> MATCH = suggestion: %o`,
                  suggestion,
                );
              }
            }
            // can't improve on exact match
            if (exact) {
              logger.out.info(
                `${this.constructor.name}.regularBlur(): SEARCH -> EXACT MATCH = suggestion: %o`,
                suggestion,
              );
              break;
            }
          }
          if (valid) {
            if (!this.checkDirtyBlur || this.value != validSuggestion) {
              inputValue = validInputValue;
              logger.out.info(
                `${this.constructor.name}.regularBlur(): SELECT -> validSuggestion: %o`,
                validSuggestion,
              );
              this.selectItem(validSuggestion);
            }
          } else {
            if (true == this.clearUnmatchedInputOnBlur) {
              // clear it out
              inputValue = this.value = this.inputEL.nativeElement.value = "";
              logger.out.info(
                `${this.constructor.name}.regularBlur(): MODEL CHANGE | NULL -> value: %o`,
                this.value,
              );
              this.onModelChange(this.value);
            }
          }
          // because of https://www.bcdfx.com/ijira/browse/OMS-33273
          if (this.inputId == "fixing-input" && !valid) {
            if (this.convertedUTCTimeFixingList.length > 0) {
              valid =
                this.convertedUTCTimeFixingList.find(
                  (utcFixing) =>
                    typeof utcFixing === "string" && COLLATOR.compare(utcFixing, inputValue) === 0,
                ) != null;
            }
          }
        } else if (true == this.clearUnmatchedInputOnBlur && this.suggestions.length == 0) {
          // clear it out
          this.value = this.inputEL.nativeElement.value = "";
          logger.out.info(
            `${this.constructor.name}.regularBlur(): MODEL CHANGE | NULL -> value: %o`,
            this.value,
          );
          this.onModelChange(this.value);
        }

        if (!valid && !emptyAsInvalid) {
          if (this.multiple) {
            this.multiInputEL.nativeElement.value = "";
            this.updateIsNotEmpty();
          } else {
            // leave what they typed in there
            this.value = inputValue;
            this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.INVALID));
          }
          logger.out.info(
            `${this.constructor.name}.regularBlur(): MODEL CHANGE -> value: %o`,
            this.value,
          );
          this.onModelChange(this.value);
        } else if (emptyAsInvalid) {
          this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.INVALID));
        } else {
          this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.VALID));
        }
      } else {
        this.stateChange(this.getAutoCompleteState(BfxAutoCompleteComponentState.INACTIVE));
      }
      logger.out.info(
        `${this.constructor.name}.regularBlur(): VALIDATION -> state: ${
          this.state in BfxAutoCompleteComponentState
            ? BfxAutoCompleteComponentState[this.state]
            : this.state
        }`,
      );
    }

    logger.out.info(`${this.constructor.name}.regularBlur(): EMIT`);
    this.onBlur.emit($event);
  }

  protected stateChange(newState: BfxAutoCompleteComponentState): void {
    this.cd.markForCheck();

    if (this.settingsCorrection) {
      if (
        this.disabled &&
        newState != BfxAutoCompleteComponentState.NONE &&
        newState != BfxAutoCompleteComponentState.INACTIVE
      ) {
        this.inputLabelState = this.disabled ? "disabled" : "enabled";
        this.fieldState = this.disabled ? "disabled" : "enabled";
        this.cd.markForCheck();
        return;
      }
    }
    if (this.state != BfxAutoCompleteComponentState.NONE && this.dataChanged === true) {
      let oldState: BfxAutoCompleteComponentState = this.state;
      this.state = newState;
      switch (this.state) {
        case BfxAutoCompleteComponentState.ACTIVE:
          this.inputLabelState = this.disabled ? "disabled" : "enabled";
          this.fieldState = this.disabled ? "disabled" : "active";
          break;
        case BfxAutoCompleteComponentState.NONE:
        case BfxAutoCompleteComponentState.INACTIVE:
          this.inputLabelState = "read-only";
          this.fieldState = "read-only";
          break;
        case BfxAutoCompleteComponentState.VALID:
          this.inputLabelState = this.disabled ? "disabled" : "enabled";
          this.fieldState = this.disabled
            ? "disabled"
            : this.fieldPointerOver || this.dropdownListOver
              ? "hover"
              : "enabled";
          break;
        case BfxAutoCompleteComponentState.INVALID:
          this.inputLabelState = this.disabled ? "disabled" : "invalid";
          this.fieldState = this.disabled ? "disabled" : "invalid";
          break;
        default:
          break;
      }
      this.markForCheck();
      this.onStateChanged.emit({ state: newState, oldState: oldState });
    }
  }

  public updateIsNotEmpty(): boolean {
    this.isInputNotEmpty = this.getInputQuery().trim().length > 0 || this.placeholder != null;

    return this.isInputNotEmpty;
  }

  isSelected(val: any): boolean {
    let selected: boolean = false;
    if (this.value && this.value.length) {
      for (let i = 0; i < this.value.length; i++) {
        if (ObjectUtils.equals(this.value[i], val, this.dataKey)) {
          selected = true;
          break;
        }
      }
    }
    return selected;
  }

  findOptionIndex(option): number {
    let index: number = -1;
    if (this.suggestions) {
      for (let i = 0; i < this.suggestions.length; i++) {
        if (ObjectUtils.equals(option, this.suggestions[i])) {
          index = i;
          break;
        }
      }
    }

    return index;
  }

  updateInputtedState() {
    const previous = this.inputted;
    let current;

    if (this.multiple) {
      current = (this.value && this.value.length > 0) || this.getInputQuery() != "";
    } else {
      current =
        this.value != null ||
        (this.inputFieldValue && this.inputFieldValue != "") ||
        this.getInputQuery() != "";
    }

    if (previous !== current) {
      this.inputted = current;
      this.updateIsNotEmpty();
      this.markForCheck();
    }
  }

  updateInputField() {
    let formattedValue = this.getOptionLabel(this.value);
    this.inputFieldValue = formattedValue;

    if (this.inputEL && this.inputEL.nativeElement) {
      this.inputEL.nativeElement.value = formattedValue;
      this.updateIsNotEmpty();
    }

    this.updateInputtedState();
  }

  bindDocumentClickListener() {
    this.afterViewInitReady.then(() => {
      if (!this.documentClickListener) {
        this.documentClickListener = this.renderer.listen(
          this.inputEL.nativeElement.ownerDocument,
          "click",
          (event) => {
            if (event.which === 3) {
              return;
            }

            if (!this.inputClick && !this._isDropdownClick(event)) {
              this.hide();
            }

            this.inputClick = false;
            this.markForCheck();
            setTimeout(() => {
              // Check to see if nothing is focused - if so, then revert focus back to the input
              // element
              const doc = DomHandler.getDocument(this.el.nativeElement);
              if (doc?.activeElement?.localName == "body") {
                if (
                  NativeDesktopSupport.isNativeDesktopContainer() &&
                  NativeDesktopSupport.enableDropdowns === true
                ) {
                  let wnd = NativeDesktopSupport.getWindow(this.el.nativeElement);

                  logger.out.debug(`Frame is ${wnd?.getId()}`);

                  if (wnd != null) {
                    wnd.focus().then(() => {
                      this.focusInput();
                    });
                  } else {
                    logger.out.warn(`${this.constructor.name}: Failed to determine parent window.`);
                    this.focusInput();
                  }
                } else {
                  this.focusInput();
                }
              }
            }, 50);
          },
        );
      }
    });
  }

  private _isPanelClick(event): boolean {
    return (
      this.appendTo == "body" &&
      event.target != null &&
      this.panelEL != null &&
      this.panelEL.nativeElement != null &&
      !(NativeDesktopSupport.isNativeDesktopContainer() && NativeDesktopSupport.enableDropdowns) &&
      (this.panelEL.nativeElement == event.target ||
        this.panelEL.nativeElement.contains(event.target))
    );
  }

  protected _isDropdownClick(event): boolean {
    if (this.dropdown) {
      let target = event.target;
      return (
        target === this.dropdownButton.nativeElement ||
        target.parentNode === this.dropdownButton.nativeElement
      );
    } else {
      return false;
    }
  }

  isDropdownClick(event): Promise<boolean> {
    return new Promise<boolean>((res) => {
      this.afterViewInitReady.then(() => {
        res(this._isDropdownClick(event));
      });
    });
  }

  unbindDocumentClickListener() {
    this.afterViewInitReady.then(() => {
      if (this.documentClickListener) {
        this.documentClickListener();
        this.documentClickListener = null;
      }
    });
  }

  ngOnDestroy() {
    this.isDestroy = true;
    this.unbindDocumentClickListener();

    if (this.appendTo && this.el != null && this.el.nativeElement != null) {
      this.el.nativeElement.appendChild(this.panelEL.nativeElement);
    }
  }

  markForCheck() {
    if (this.isDestroy == false) {
      this.cd.markForCheck();
    }
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxFieldSelect(): Record<string, any> {
    return this.fieldDirective.directiveHost(this);
  }

  onFieldIconClick($event, type, index) {
    if (type === "end" && index === 0) {
      this.handleDropdownClick($event);
    }
    this.fieldIconClick.next({ event: $event, type: type, index: index });
  }

  public reset(): void {
    this.value = null;

    this.suggestions = [];

    this.state = BfxAutoCompleteComponentState.INACTIVE;
  }
}
